import styled from '@emotion/styled';
import React, { useState } from 'react';
import { border_radius_styles_with_size, semantic_colors } from '../../foundation';
import { Divider } from '../divider';
import { Controller } from './Controller';
import { AccordionButton } from '../button';
import { Button } from '../button';
import { ColorPickerTrigger } from './ColorPickerTrigger';
import { ColorPickerSize, PresetType } from './types';
import { Preset } from './Preset';
import {
  FloatingPortal,
  offset,
  shift,
  flip,
  autoUpdate,
  hide,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useClick,
} from '@floating-ui/react';
import { FLOATING_ROOT_ID } from '../../constants';
import classNames from 'classnames';
import { removeAlpha } from './utils';
import { IconReset } from '../icons/generated';
import { getZIndex } from '../../styles';

export type ColorPickerOpenerTriggerEvent = 'click' | 'none';
export type ColorPickerLayout = 'custom' | 'preset';
export interface ColorPickerProps {
  /**
   * hex color code (ex: #FFFFFF)
   */
  defaultValue?: string;
  /**
   * hex color code (ex: #FFFFFF)
   */
  value?: string;
  /**
   * @param code hex color code (ex: FFFFFF, #FFFFFF6D)
   */
  onChange(code: string): void;
  disabled?: boolean;
  /** @default 'medium' */
  size?: ColorPickerSize;
  /**
   * preset props이 없으면 layout=preset이 적용되지 않습니다.
   * @default 'custom'
   */
  layout?: ColorPickerLayout;
  alpha?: boolean;
  /** @default false */
  hueOnly?: boolean;
  preset?: PresetType[];
  /** @default '초기화' */
  resetText?: string;
  /** @default '상세 설정' */
  settingText?: string;
  /** @default getZIndex('picker') */
  zIndex?: number;
  className?: string;
  /**
   * 'none'으로 설정할 경우 preset popover가 열리지 않습니다.
   * @default 'click'
   */
  openerTriggerEvent?: ColorPickerOpenerTriggerEvent;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  defaultValue = '',
  value = defaultValue || '',
  onChange,
  disabled,
  size,
  layout = 'custom',
  alpha,
  hueOnly = false,
  preset,
  resetText = '초기화',
  settingText = '상세 설정',
  zIndex = getZIndex('picker'),
  className,
  openerTriggerEvent = 'click',
}) => {
  const [opened, setOpened] = useState(false);
  const [preset_opened, setPresetOpened] = useState(false);
  const preset_first = layout === 'preset' && preset && preset?.length > 0;
  const can_open_preset = openerTriggerEvent === 'click';
  const { refs, context, floatingStyles } = useFloating({
    open: opened,
    onOpenChange: setOpened,
    placement: 'bottom-start',
    middleware: [offset(4), shift(), flip(), hide()],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: can_open_preset }),
    useRole(context),
    useDismiss(context, { outsidePress: true }),
  ]);
  const converted_preset = preset?.map((preset) => ({
    ...preset,
    colors: preset.colors.map((color) => {
      const no_alpha = removeAlpha(color.color);
      const matched = alpha || no_alpha === color.color;
      return {
        ...color,
        color: alpha ? color.color : no_alpha,
        disabled: matched ? color.disabled : true,
      };
    }),
  }));

  const handleOpenDetailToggle = () => {
    setPresetOpened((prev_opened) => !prev_opened);
  };

  const handleReset = () => {
    onChange((alpha ? defaultValue : removeAlpha(defaultValue)).toUpperCase());
  };

  const handleChange = (value: string) => {
    onChange(alpha ? value : removeAlpha(value));
  };

  return (
    <>
      <ColorPickerTrigger
        size={size}
        color={value}
        can_open_preset={can_open_preset}
        onClick={() => can_open_preset && !disabled && setOpened((prev) => !prev)}
        disabled={disabled}
        opened={opened}
        type='button'
        aria-label='Color picker'
        {...getReferenceProps({
          ref: refs.setReference,
        })}
      />
      {opened && (
        <FloatingPortal id={FLOATING_ROOT_ID}>
          <Panel
            role='dialog'
            aria-label='Color picker panel'
            {...getFloatingProps({
              ref: refs.setFloating,
              className: classNames('pds-colorpicker', className),
              style: {
                ...floatingStyles,
                zIndex,
              },
            })}
          >
            <Button
              variant='outlined'
              color='secondary'
              leftIcon={<IconReset />}
              size='xsmall'
              style={{
                width: 'fit-content',
              }}
              onClick={handleReset}
              data-testid='color-picker-reset-button'
              aria-label='Reset color'
            >
              {resetText}
            </Button>
            {preset_first ? (
              <>
                <Preset preset={converted_preset} selected_color={value} onSelect={handleChange} />
                <AccordionButton open={preset_opened} onClick={handleOpenDetailToggle} size='xsmall'>
                  {settingText}
                </AccordionButton>
                {preset_opened && (
                  <Controller
                    code={value}
                    onCodeChange={handleChange}
                    support_alpha={alpha}
                    hueOnly={hueOnly}
                    zIndex={zIndex}
                  />
                )}
              </>
            ) : (
              <>
                <Controller
                  code={value}
                  onCodeChange={handleChange}
                  support_alpha={alpha}
                  hueOnly={hueOnly}
                  zIndex={zIndex}
                />
                {converted_preset && converted_preset?.length > 0 && (
                  <>
                    <Divider />
                    <Preset preset={converted_preset} selected_color={value} onSelect={handleChange} />
                  </>
                )}
              </>
            )}
          </Panel>
        </FloatingPortal>
      )}
    </>
  );
};

const Panel = styled.div`
  width: 312px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  border-radius: ${border_radius_styles_with_size.small}px;
  background: ${semantic_colors.background.elevated};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.1);
`;
